import { noop } from '@proc7ts/primitives';
import { Supply } from '@proc7ts/supply';
import { eventReceiver, OnEvent__symbol, onEventBy } from '@proc7ts/fun-events';

/**
 * @internal
 * @hidden
 */
const OnDomEvent$sample = ( /*#__PURE__*/onEventBy(noop));
/**
 * Converts a plain DOM event listener registration function to {@link OnDomEvent} sender.
 *
 * @typeParam TEvent - Supported DOM event type.
 * @param register - A generic DOM event listener registration function.
 *
 * @returns An {@link OnDomEvent} sender registering event listeners with the given `register` function.
 */
function onDomEventBy(register) {
    const onDomEvent = ((listener, opts) => {
        const receiver = eventReceiver(listener);
        const { supply } = receiver;
        if (!supply.isOff) {
            register(receiver, opts);
        }
        return supply;
    });
    onDomEvent[OnEvent__symbol] = OnDomEvent$sample[OnEvent__symbol];
    onDomEvent.do = OnDomEvent$sample.do;
    onDomEvent.then = OnDomEvent$sample.then;
    return onDomEvent;
}

/**
 * @internal
 * @hidden
 */
const OnDomEvent$context = {
    onRecurrent: noop,
};
/**
 * DOM event dispatcher can be used to register event listeners of particular event types and dispatch events.
 */
class DomEventDispatcher {
    /**
     * Constructs DOM event dispatcher for the given event target.
     *
     * @param target - Event target to construct event dispatcher for.
     */
    constructor(target) {
        this.supply = new Supply();
        this._target = target;
    }
    /**
     * Returns a sender of DOM events of the given `type`.
     *
     * The returned DOM event sender calls an `EventTarget.addEventListener()` to register listeners.
     * But, in contrast, it allows to register the same listener many times.
     *
     * The {@link Supply event supply} returned upon event listener registration unregisters it with
     * `EventTarget.removeEventListener()` once {@link Supply.off cut off}.
     *
     * @typeParam TEvent - Supported DOM event type.
     * @param type - DOM event type name.
     *
     * @returns {@link OnDomEvent} sender of DOM events of the given `type`.
     */
    on(type) {
        return onDomEventBy((listener, opts) => {
            const { supply } = listener;
            supply.needs(this);
            if (!supply.isOff) {
                // Create unique DOM listener instance
                const domListener = event => listener.receive(OnDomEvent$context, event);
                this._target.addEventListener(type, domListener, opts);
                listener.supply.whenOff(() => this._target.removeEventListener(type, domListener));
            }
        });
    }
    /**
     * Dispatches the given DOM event to event target.
     *
     * Calls `EventTarget.dispatchEvent()` method.
     *
     * @param event - An event to dispatch.
     *
     * @returns `true` if either event's `cancelable` attribute value is `false` or its `preventDefault()` method was not
     * invoked, or `false` otherwise. Also returns `false` when {@link supply} is cut off.
     */
    dispatch(event) {
        return !this.supply.isOff && this._target.dispatchEvent(event);
    }
}

/**
 * Creates an {@link OnDomEvent} sender that enables event capturing by default.
 *
 * This corresponds to specifying `true` or `{ capture: true }` as a second argument to
 * `EventTarget.addEventListener()`.
 *
 * @typeParam TEvent - DOM event type.
 * @param supplier - DOM events sender.
 *
 * @returns DOM events sender.
 */
function captureDomEvents(supplier) {
    return onDomEventBy((listener, opts) => {
        if (opts == null) {
            return supplier(listener, true);
        }
        if (typeof opts === 'object' && opts.capture == null) {
            return supplier(listener, { ...opts, capture: true });
        }
        return supplier(listener, opts);
    });
}

/**
 * Creates a DOM events processor that enables or disables default DOM event handlers.
 *
 * @typeParam TEvent - DOM event type.
 * @param enable - Whether to enable default handlers. `true` to enable (default value, corresponds to specifying
 * `{ passive: true }` as a second argument to `EventTarget.addEventListener()`), or `false` to disable
 * (causes listeners to invoke an `Event.preventDefault()` method prior to event handling).
 *
 * @returns {@link OnDomEvent} mapper function.
 */
function handleDomEvents(enable = true) {
    return enable ? listenDomEventsPassively : preventDefaultDomEventHandler;
}
/**
 * @internal
 * @hidden
 */
function listenDomEventsPassively(supplier) {
    return onDomEventBy((listener, opts) => {
        if (opts == null) {
            return supplier(listener, { passive: true });
        }
        if (typeof opts === 'boolean') {
            return supplier(listener, { capture: opts, passive: true });
        }
        if (opts.passive == null) {
            return supplier(listener, { ...opts, passive: true });
        }
        return supplier(listener, opts);
    });
}
/**
 * @internal
 * @hidden
 */
function preventDefaultDomEventHandler(supplier) {
    return onDomEventBy((listener, opts) => {
        const receiver = eventReceiver(listener);
        return supplier({
            supply: receiver.supply,
            receive(context, event) {
                event.preventDefault();
                receiver.receive(context, event);
            },
        }, opts);
    });
}

/**
 * Creates an {@link OnDomEvent} sender preventing other listeners of the same event from being called.
 *
 * Causes listeners to invoke an [Event.stopImmediatePropagation()] method prior to event handing.
 *
 * [Event.stopImmediatePropagation()]: https://developer.mozilla.org/en-US/docs/Web/API/Event/stopImmediatePropagation
 *
 * @typeParam TEvent - DOM event type.
 * @param supplier - DOM events sender.
 *
 * @returns DOM events sender.
 */
function interceptDomEvents(supplier) {
    return onDomEventBy((listener, opts) => {
        const receiver = eventReceiver(listener);
        return supplier({
            supply: receiver.supply,
            receive(context, event) {
                event.stopImmediatePropagation();
                receiver.receive(context, event);
            },
        }, opts);
    });
}

/**
 * Creates an {@link OnDomEvent} sender preventing further propagation of events in the capturing and bubbling phases.
 *
 * Causes listeners to invoke an [Event.stopPropagation()] method prior to event handing.
 *
 * [Event.stopPropagation()]: https://developer.mozilla.org/en-US/docs/Web/API/Event/stopPropagation
 *
 * @typeParam TEvent - DOM event type.
 * @param supplier - DOM events sender.
 *
 * @returns DOM events sender.
 */
function stopDomEvents(supplier) {
    return onDomEventBy((listener, opts) => {
        const receiver = eventReceiver(listener);
        return supplier({
            supply: receiver.supply,
            receive(context, event) {
                event.stopPropagation();
                receiver.receive(context, event);
            },
        }, opts);
    });
}

export { DomEventDispatcher, captureDomEvents, handleDomEvents, interceptDomEvents, onDomEventBy, stopDomEvents };
//# sourceMappingURL=dom-events.js.map
